import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

interface VerificationInProgressDialogProps {
  open: boolean;
  onClose: () => void;
  hasExistingFacilities?: boolean;
  onOk?: () => void;
  onContactSupport?: () => void;
  showContactSupportButton?: boolean;
  /** Optional dialog layout overrides so different screens can tweak sizing */
  dialogConfig?: {
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
    fullWidth?: boolean;
    paperSx?: any;
  };
}

const VerificationInProgressDialog: React.FC<
  VerificationInProgressDialogProps
> = ({
  open,
  onClose,
  hasExistingFacilities,
  onOk,
  onContactSupport,
  showContactSupportButton,
  dialogConfig,
}) => {
  const handleOkClick = () => {
    if (onOk) {
      onOk();
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      fullWidth={dialogConfig?.fullWidth ?? true}
      maxWidth={dialogConfig?.maxWidth ?? "sm"}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          border: "1px solid #CCF5FF",
          backgroundColor: "#fffdfd",
          boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
          ...(dialogConfig?.paperSx || {}),
        },
      }}
    >
      <DialogTitle sx={{ p: 2, borderBottom: "1px solid #BFE5EC" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "999px",
                backgroundColor: "#E0F6FB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InfoOutlined sx={{ fontSize: 18, color: "#0A829D" }} />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: "18px",
                color: "#065B70",
              }}
            >
              Verification in Progress
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 0,
              p: 0,
              color: "#065B70",
              textTransform: "none",
            }}
          >
            ✕
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          fontFamily: '"Rethink Sans", sans-serif',
        }}
      >
        <Typography
          sx={{
            color: "#262626",
            fontSize: "14px",
            mt: 2,
            mb: 2,
            lineHeight: 1.6,
          }}
        >
          We are verifying your account and the selected facility. This process
          may take up to 24 hours. You’ll receive an email once verification is
          complete with instructions on next steps.
        </Typography>
        {hasExistingFacilities && (
          <Typography
            sx={{
              color: "#4b5563",
              fontSize: "13px",
              fontFamily: '"Rethink Sans", sans-serif',
            }}
          >
            You can continue using the platform for your existing facilities
            while verification is in progress.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}
      >
        <Button
          onClick={handleOkClick}
          variant={showContactSupportButton ? "outlined" : "contained"}
          sx={
            showContactSupportButton
              ? {
                  textTransform: "none",
                  borderColor: "#065b70",
                  color: "#065b70",
                  "&:hover": {
                    borderColor: "#054a5a",
                    backgroundColor: "#e6f7fa",
                  },
                }
              : {
                  textTransform: "none",
                  backgroundColor: "#065b70",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#054a5a",
                  },
                }
          }
        >
          OK
        </Button>

        {showContactSupportButton && onContactSupport && (
          <Button
            onClick={onContactSupport}
            variant="contained"
            sx={{
              backgroundColor: "#065b70",
              color: "#ffffff",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#054a5a",
              },
            }}
          >
            Contact Support
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VerificationInProgressDialog;


